import { useState, useEffect, useRef } from 'react';

/**
 * Hook that animates a number from `start` to `end` over `duration` ms.
 * Uses requestAnimationFrame with an ease-out-quart curve.
 */
export function useAnimatedCounter(end, duration = 2000, start = 0) {
  const [count, setCount] = useState(start);
  const countRef = useRef(start);
  const animationRef = useRef(null);

  useEffect(() => {
    if (end === null || end === undefined) return;

    const startTime = Date.now();
    const startValue = countRef.current;
    const difference = end - startValue;

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const current = startValue + difference * easeOutQuart;

      countRef.current = current;
      setCount(Math.round(current));

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [end, duration]);

  return count;
}
